/**
 * PayPalPayLater — bannière « Payez en 4× » (PayPal Pay Later messaging).
 *
 * Affiche uniquement le MESSAGE promotionnel PayPal (module `messages`) : il informe le client
 * qu'il peut régler en 4 fois et montre le détail des échéances selon le montant. Il ne TRAITE
 * pas le paiement (l'encaissement réel PayPal sera un chantier séparé).
 *
 * Chargement paresseux du SDK PayPal (une seule fois), rendu non bloquant : en cas d'échec de
 * chargement ou si Pay Later n'est pas éligible, aucune bannière n'apparaît et rien ne casse.
 */
import React, { useEffect, useRef } from 'react';

const PP_CLIENT_ID =
  process.env.REACT_APP_PAYPAL_CLIENT_ID ||
  'AQ34FSEytZRyrDws6uGdbywhW8PUTvwWFC0zwYjsgtCkJqdu6jJoi1ZGPgxRTfwrlk_ddCvEFmCAznQP';
const PP_NAMESPACE = 'PayPalSDK';
const PP_SRC = `https://www.paypal.com/sdk/js?client-id=${PP_CLIENT_ID}&currency=EUR&components=messages`;

let _loadPromise = null;
function loadPayPalMessages() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('no-dom'));
  }
  if (window[PP_NAMESPACE] && window[PP_NAMESPACE].Messages) {
    return Promise.resolve(window[PP_NAMESPACE]);
  }
  if (_loadPromise) return _loadPromise;
  _loadPromise = new Promise((resolve, reject) => {
    try {
      const done = () => (window[PP_NAMESPACE] ? resolve(window[PP_NAMESPACE]) : reject(new Error('sdk-absent')));
      const existing = document.querySelector(`script[data-namespace="${PP_NAMESPACE}"]`);
      if (existing) {
        existing.addEventListener('load', done);
        existing.addEventListener('error', () => { _loadPromise = null; reject(new Error('sdk-error')); });
        if (window[PP_NAMESPACE]) resolve(window[PP_NAMESPACE]);
        return;
      }
      const s = document.createElement('script');
      s.src = PP_SRC;
      s.async = true;
      s.setAttribute('data-namespace', PP_NAMESPACE);
      s.onload = done;
      s.onerror = () => { _loadPromise = null; reject(new Error('sdk-error')); };
      document.head.appendChild(s);
    } catch (e) {
      _loadPromise = null;
      reject(e);
    }
  });
  return _loadPromise;
}

const PayPalPayLater = ({
  amount,
  placement = 'product',
  layout = 'text',
  logoType = 'inline',
  textColor = 'black',
  className,
  style,
}) => {
  const ref = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const amt = Number(amount);
    // Débounce : le montant du panier peut changer plusieurs fois (quantité, remise). On rend le message
    // une fois le montant stabilisé, et on rend dans un enfant NEUF à chaque fois pour que PayPal recalcule
    // réellement les échéances sur le montant courant (sinon il garde l'ancien montant = bug 79 vs 395).
    const timer = setTimeout(() => {
      loadPayPalMessages()
        .then((sdk) => {
          if (cancelled || !ref.current || !sdk || !sdk.Messages) return;
          try {
            const cfg = {
              placement,
              style: { layout, logo: { type: logoType }, text: { color: textColor } },
            };
            if (Number.isFinite(amt) && amt > 0) cfg.amount = amt;
            ref.current.innerHTML = '';
            const child = document.createElement('div');
            ref.current.appendChild(child);
            sdk.Messages(cfg).render(child);
          } catch (_) {
            /* messaging non bloquant */
          }
        })
        .catch(() => {
          /* SDK indisponible -> pas de bannière, silencieux */
        });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [amount, placement, layout, logoType, textColor]);

  return <div ref={ref} className={className} style={style} data-testid="paypal-paylater" />;
};

export default PayPalPayLater;
